import { QueryClient } from "@tanstack/react-query";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { PersistedClient } from "@tanstack/react-query-persist-client";
import { del, get, set } from "idb-keyval";

export const queryClient = new QueryClient();

const PERSIST_CACHE_KEY = "OO_RQ_CACHE";

/**
 * IndexedDB persister (via idb-keyval) for the React Query cache. Only a
 * hand-picked set of queries is actually written (see `shouldDehydrateQuery`
 * in App.tsx) — the organisation entity, the current user's preferences and
 * the full consultant/customer lists — so reloads render instantly from the
 * persisted cache. IndexedDB stores structured clones, so serialize/
 * deserialize are identity functions and the multi-MB list cache never goes
 * through JSON.stringify. The key is removed on logout via
 * `clearPersistedCache()` in AuthStore.onLogoutCleanup.
 */
export const queryPersister = createAsyncStoragePersister({
	storage: {
		// idb-keyval resolves undefined for a missing key; the persister expects null.
		getItem: async (key) => (await get(key)) ?? null,
		setItem: (key, value) => set(key, value),
		removeItem: (key) => del(key),
	},
	key: PERSIST_CACHE_KEY,
	// The persister's types assume string (de)serialization, but IndexedDB
	// happily stores the cache object as-is — hence the identity casts.
	serialize: (client) => client as unknown as string,
	deserialize: (cachedString) => cachedString as unknown as PersistedClient,
});

/** Remove the persisted React Query cache from IndexedDB (used on logout). */
export function clearPersistedCache(): Promise<void> {
	return del(PERSIST_CACHE_KEY);
}

/** How long persisted queries are allowed to live in the persisted cache. */
export const PERSIST_MAX_AGE = 24 * 60 * 60 * 1000;

export type QueryOpts<T = any> = {
	enabled?: boolean;
	refetchInterval?: number;
	refetchOnWindowFocus?: boolean;
};
